import { Chip, Typography } from '@mui/material';
import { isEmpty, isNumber } from 'lodash';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { DescriptionRow, StylizedNumber } from 'components';
import { P2PProviderType, PayoutProtectionStatus } from 'enums';
import { P2P_PROVIDER_TYPES_WITH_NO_PAYOUT_PROTECTION } from 'features/p2p-providers/P2PProviderDetailsPage/constants';
import { TranslationNamespace } from 'i18n';
import { P2PProvider } from 'types';
import { formatUtils } from 'utils';

import { protectionStatusColor } from '../../P2PProviderDetailsPage/utils';
import { ExpandableTradeMethods } from '../ExpandableTradeMethods';
import { P2PProviderCredentialsDetails } from '../P2PProviderCredentialsDetails';
import { P2PProviderOrderAmountLimitsDetails } from '../P2PProviderOrderAmountLimitsDetails';

type Props = {
  p2pProvider: P2PProvider;
};

export const P2PProviderPayoutConfigDetails: React.FC<Props> = ({
  p2pProvider,
}) => {
  const { fiatCurrencyId, payoutConfig: config } = p2pProvider;

  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.p2p_providers',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const protectionStatus =
    config?.payoutProtectionStatus ?? PayoutProtectionStatus.Legacy;

  return (
    <Fragment>
      <Typography variant="h6">{t('details.payout_details')}</Typography>
      <DescriptionRow
        title={t('details.status.title')}
        value={
          config?.enabled
            ? t('details.status.enabled')
            : t('details.status.disabled')
        }
      />
      {!P2P_PROVIDER_TYPES_WITH_NO_PAYOUT_PROTECTION.includes(
        p2pProvider.type as P2PProviderType,
      ) && (
        <DescriptionRow
          title={t('fields.payout_protection_status')}
          value={
            <Chip
              size="small"
              variant="outlined"
              color={protectionStatusColor[protectionStatus]}
              label={String(t(`fields.protection_status.${protectionStatus}`))}
            />
          }
        />
      )}
      {config?.url && (
        <DescriptionRow title={t('details.url')} value={config.url} canCopy />
      )}

      <P2PProviderCredentialsDetails credentials={config?.credentials} />

      {!!config?.responseTimeout && (
        <DescriptionRow
          title={t('details.response_timeout')}
          description={t('details.response_timeout_description')}
          value={`${formatUtils.formatNumber(config.responseTimeout)}${tCommon(
            'suffixes.ms',
          )}`}
        />
      )}
      {!!config?.orderTimeout && (
        <DescriptionRow
          title={t('details.order_timeout')}
          description={t('details.order_timeout_description')}
          value={`${formatUtils.formatNumber(config.orderTimeout)}${tCommon(
            'suffixes.minutes',
          )}`}
        />
      )}
      {isNumber(config?.expectedFee) && (
        <DescriptionRow
          title={t('details.expected_fee')}
          value={<StylizedNumber value={config.expectedFee} unit="%" />}
        />
      )}

      {config?.lastTakenOrderAt && (
        <DescriptionRow
          title={t('details.last_taken_order_at')}
          value={formatUtils.formatDate(config.lastTakenOrderAt)}
        />
      )}

      {config?.tradeMethods && !isEmpty(config.tradeMethods) && (
        <DescriptionRow
          title={t('details.trade_methods')}
          value={<ExpandableTradeMethods tradeMethods={config.tradeMethods} />}
        />
      )}

      {!P2P_PROVIDER_TYPES_WITH_NO_PAYOUT_PROTECTION.includes(
        p2pProvider.type as P2PProviderType,
      ) && (
        <Fragment>
          {config?.payoutResponseTemplates?.statusFieldPath && (
            <DescriptionRow
              title={t('fields.status_field_path')}
              value={config.payoutResponseTemplates.statusFieldPath}
              canCopy
            />
          )}
          {(config?.payoutResponseTemplates?.acceptedStatuses?.length ?? 0) >
            0 && (
            <DescriptionRow
              title={t('fields.accepted_statuses')}
              value={
                config?.payoutResponseTemplates?.acceptedStatuses?.join(', ') ??
                ''
              }
            />
          )}
          {(config?.payoutResponseTemplates?.rejectedStatuses?.length ?? 0) >
            0 && (
            <DescriptionRow
              title={t('fields.rejected_statuses')}
              value={
                config?.payoutResponseTemplates?.rejectedStatuses?.join(', ') ??
                ''
              }
            />
          )}
          {(config?.payoutResponseTemplates?.requiredResponseFields?.length ??
            0) > 0 && (
            <DescriptionRow
              title={t('fields.required_response_fields')}
              value={
                config?.payoutResponseTemplates?.requiredResponseFields?.join(
                  ', ',
                ) ?? ''
              }
            />
          )}
          {(config?.payoutResponseTemplates?.rejectedHttpStatusCodes?.length ??
            0) > 0 && (
            <DescriptionRow
              title={t('fields.rejected_http_status_codes')}
              value={
                config?.payoutResponseTemplates?.rejectedHttpStatusCodes?.join(
                  ', ',
                ) ?? ''
              }
            />
          )}
          {(config?.payoutResponseTemplates?.acceptanceIndicators?.length ??
            0) > 0 && (
            <DescriptionRow
              title={t('fields.acceptance_indicators')}
              value={JSON.stringify(
                config?.payoutResponseTemplates?.acceptanceIndicators,
              )}
            />
          )}
          {config?.templatesConfiguredAt && (
            <DescriptionRow
              title={t('fields.templates_configured_at')}
              value={formatUtils.formatDate(config.templatesConfiguredAt)}
            />
          )}
        </Fragment>
      )}

      <P2PProviderOrderAmountLimitsDetails
        fiatCurrencyId={fiatCurrencyId}
        config={config}
      />
    </Fragment>
  );
};
